import { useState } from "react";
import dayjs from "@shared/dayjs";
// import Avatar from "@shared/v2/Avatar";
import Checkbox from "@shared/v2/Checkbox";
import {
  BoltLightningSolidV6,
  ListOlSolidV6,
  PencilSolidV6,
  TrashSolidV6,
  SpinnerSolidV6,
} from "@shared/v2/Icomoon";
import IconButton from "@shared/v2/IconButton";
import Typography from "@shared/v2/Typography";
import { Task } from "./types";
import { Popover } from "@shared/v2/Popover";
import ShadowScroll from "@shared/v2/ShadowScroll";
import Button from "@shared/v2/Button";

interface ConferenceTaskCardProps {
  task: Task;
  onToggleComplete: (task: Task) => void;
  onDelete: (task: Task) => void;
  onEdit: (task: Task) => void;
}

const getDuenessColor = (dueness: string) => {
  switch (dueness) {
    case "overdue":
      return "!tw-text-[#E5342E]";
    case "due_today":
      return "!tw-text-yellow-100";
    default:
      return "!tw-text-gray-50";
  }
};

const formatDueOnRelative = (dueOn: string) => {
  const dueDate = dayjs(dueOn);
  const today = dayjs();

  if (dueDate.isSame(today, "day")) {
    return "Today";
  }

  return dueDate.fromNow(dueDate.isAfter(today) ? true : false);
};

const ConferenceTaskCard = ({
  task,
  onToggleComplete: onCompleteTask,
  onEdit,
  onDelete,
}: ConferenceTaskCardProps) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const isCompleted = task.dueness === "completed";
  return (
    <div className="tw-relative tw-bg-white tw-shadow-card tw-rounded-[8px] tw-border-solid tw-border-[1px] tw-border-gray-10 tw-px-[16px] tw-py-[12px] tw-flex tw-items-center tw-gap-[16px]">
      <div className="tw-relative tw-flex-1 tw-flex tw-items-center tw-gap-[16px]">
        {task.loading ? (
          <SpinnerSolidV6 className="tw-animate-spin" size="l" />
        ) : (
          // @ts-expect-error props
          <Checkbox checked={isCompleted} onChange={() => onCompleteTask(task)} />
        )}
        <div className="tw-relative tw-flex-1 tw-flex tw-items-center tw-gap-[16px]">
          <div className="tw-flex-1 tw-flex tw-flex-col tw-gap-[2px]">
            {/* @ts-expect-error props */}
            <Typography variant="subtitle-m">{task.name}</Typography>
            <div className="tw-flex tw-items-center tw-gap-[6px]">
              {task.assignedPlanId && <BoltLightningSolidV6 />}
              {/* @ts-expect-error props */}
              <Typography variant="subtitle-s" className={getDuenessColor(task.dueness)}>
                {formatDueOnRelative(task.dueOn)} - {dayjs(task.dueOn).format("MM/DD/YYYY")}{" "}
                {task.scheduledTime && `@ ${dayjs.unix(task.scheduledTime).format("h:mm A")}`}
              </Typography>
              {task.instructions && (
                <Popover
                  onOpenChange={setShowInstructions}
                  open={showInstructions}
                  maxHeight={172}
                  placement="bottom-start"
                >
                  {/* @ts-expect-error props */}
                  <Popover.Trigger>
                    <button
                      type="button"
                      className={`tw-border-0 tw-w-[16px] tw-h-[16px] tw-p-0 tw-flex tw-justify-center tw-items-center tw-rounded-[4px] tw-text-theme-brand-light hover:tw-text-theme-brand ${showInstructions ? "tw-bg-theme-brand-lighter" : "tw-bg-white"}`}
                      onClick={() => setShowInstructions((prev) => !prev)}
                    >
                      <ListOlSolidV6 />
                    </button>
                  </Popover.Trigger>
                  {/* @ts-expect-error props */}
                  <Popover.Content className="tw-w-[280px] tw-h-[170px] tw-p-[16px] tw-flex tw-flex-col tw-gap-[4px]">
                    {/* @ts-expect-error props */}
                    <Typography variant="subtitle-s">Instructions</Typography>
                    <ShadowScroll className="tw-h-[118px]">{task.instructions}</ShadowScroll>
                  </Popover.Content>
                </Popover>
              )}
            </div>
          </div>
          {/* TODO: Wireup in separate ticket */}
          {/* <Avatar /> */}
          <div className="tw-flex tw-flex-none tw-justify-center tw-items-center tw-w-[24px] tw-h-[24px] tw-rounded-full tw-border-solid tw-border-[1px] tw-border-gray-50 tw-text-gray-50">
            {task.priority}
          </div>
          {isCompleted && (
            <div className="tw-absolute tw-inset-0 tw-bg-white tw-opacity-50 tw-flex tw-justify-end tw-items-center tw-gap-[4px]"></div>
          )}
        </div>
        {showDeleteConfirm && (
          <div className="tw-absolute tw-inset-0 tw-bg-white tw-opacity-50 tw-flex tw-justify-end tw-items-center tw-gap-[4px]"></div>
        )}
        {!isCompleted && !showDeleteConfirm && (
          <div className="tw-relative tw-flex tw-gap-[8px] tw-pl-[8px]">
            {/* @ts-expect-error props */}
            <IconButton schema="tertiary" size="small" onClick={() => setShowDeleteConfirm(true)}>
              <TrashSolidV6 size="s" />
            </IconButton>
            {/* @ts-expect-error props */}
            <IconButton size="small" onClick={() => onEdit(task)}>
              <PencilSolidV6 size="s" />
            </IconButton>
          </div>
        )}
        {showDeleteConfirm && (
          <div className="tw-flex tw-gap-[4px] tw-pl-[8px]">
            {/* @ts-expect-error props */}
            <Button schema="tertiary" onClick={() => setShowDeleteConfirm(false)} disabled={task.loading}>
              Cancel
            </Button>
            {/* @ts-expect-error props */}
            <Button schema="warning" onClick={() => onDelete(task)} isLoading={task.loading}>
              Yes, Delete
            </Button>
          </div>
        )}
      </div>
      {isCompleted && <span className="tw-italic tw-text-gray-50">Done</span>}
    </div>
  );
};

export default ConferenceTaskCard;
